import { Mesh, Vector3 } from "three";
import { ICollidable } from "../../../../../../three/physics/ICollidable";
import { GameViewModel } from "../../../../models/GameViewModel";
import { CharacterProjectile } from "./CharacterProjectile";
import { IProjectileContainer } from "../../gamefield/IProjectileContainer";
import { BaseMole } from "../../gamefield/mole/BaseMole";
import { Box } from "../../gamefield/box/Box";
import { Level } from "../../Level";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { BatchedRenderer } from "three.quarks";

export class Boomerang extends CharacterProjectile {
    private static readonly MAX_TARGET_COUNT = 3;
    private state: BoomerangState;
    private _hitCount: number = 0;
    private get hitCount(): number {
        return this._hitCount;
    }
    private set hitCount(value: number) {
        this._hitCount = value;
        if (this._hitCount >= Boomerang.MAX_TARGET_COUNT) {
            this.destroy();
            return;
        }
        this.velocity.copy(this.tryFindNextDirection());
    }
    private currentAttackingIndex: number;
    
    public constructor(
        gameResources: GameResourcesPackage,
        particlesBatchRenderer: BatchedRenderer,
        projectileContainer: IProjectileContainer,
        private readonly gameViewModel: GameViewModel,
        protected readonly weaponMesh: Mesh,
        protected readonly speed: number,
    ) {
        super(gameResources, particlesBatchRenderer, projectileContainer, weaponMesh, speed, 200);
        this.state = BoomerangState.IDLE;
        this.currentAttackingIndex = -1;
    }

    private tryFindNextDirection(): Vector3 {
        const targets = [...this.gameViewModel.aliveSpawnedMoles, ...this.gameViewModel.aliveBoxes];
        if (targets.length === 0) {
            this.state = BoomerangState.DEACTIVATED;
            return this.velocity;
        } else {
            const fieldsIndicesExceptCurrent = targets.map(target => target.fieldIndex);
            const findIndexToAttack = this.gameViewModel.getNearestIndexToCoordinates(this.position, fieldsIndicesExceptCurrent, this.currentAttackingIndex);
            if (findIndexToAttack != -1) {
                this.currentAttackingIndex = findIndexToAttack;
            } else {
                this.state = BoomerangState.DEACTIVATED;
                return this.velocity;
            }
        }
        const temp = this.gameViewModel.indexToGameCoords(this.currentAttackingIndex).sub(this.position);
        return temp.normalize().multiplyScalar(this.speed);
    }

    public override throwAtDirection(direction: Vector3): void {
        if (this.state === BoomerangState.IDLE) {
            this.state = BoomerangState.THROWN;
            this.weaponMesh.position.y += 0.2;
        }
        super.throwAtDirection(direction);
    }

    protected override onTick(dt: number): void {
        this.rotate(dt);
        super.onTick(dt);
    }

    private rotate(dt: number): void {
        this.weaponMesh.rotateY(dt * .1);
    }

    public override onCollision(other: ICollidable): void {
        switch (this.state) {
            case BoomerangState.IDLE: {
                break;
            }
            case BoomerangState.THROWN: {
                if (other instanceof BaseMole || other instanceof Box) {
                    if (other.alive) {
                        other.takeDamage(this.damage);
                        this.hitCount++;
                    }
                }
                if (other instanceof Level) {
                    this.state = BoomerangState.DEACTIVATED;
                }
                break;
            }
            case BoomerangState.DEACTIVATED: {
                this.projectileContainer.remove(this);
                this.destroy();
                break;
            }
            default: {
                console.warn("Unknown Boomerang state!");
            }
        }
    }

    public override throwToPoint(target: Vector3): void {
        const direction = target.clone().sub(this.getWorldPosition(new Vector3())).normalize();
        this.throwAtDirection(direction);
    }

    public override move(dt: number): void {
        this.movement.copy(this.velocity).multiplyScalar(dt);
        this.position.add(this.movement);
    }

    public override destroy(): void {
        this.removeFromParent();
        super.destroy();
    }
}

enum BoomerangState {
    IDLE = "IDLE",
    THROWN = "THROWN",
    DEACTIVATED = "DEACTIVATED"
}